/**
 * Retry policy shared by the HTTP layer and the realtime stream (prompt.md §15,
 * §49, §76).
 *
 * WHY one module: an HTTP GET that is retried after a blip and a dropped event
 * socket that reconnects both need *the same* backoff with jitter, and giving
 * each its own schedule is how clients end up thundering-herding a recovering
 * platform. `random` is injected so the schedule is deterministic in tests.
 */

export interface RetryPolicy {
  /** Total tries, including the first one. */
  readonly maxAttempts: number;
  readonly baseDelayMs: number;
  readonly maxDelayMs: number;
  /** Fraction of the delay that is randomised, in `[0, 1)`. */
  readonly jitterRatio: number;
}

export const DEFAULT_RETRY_POLICY: RetryPolicy = {
  maxAttempts: 3,
  baseDelayMs: 250,
  maxDelayMs: 30_000,
  jitterRatio: 0.5,
};

/** Backoff for a stream that should keep trying for as long as the run lives. */
export const STREAM_RETRY_POLICY: RetryPolicy = {
  maxAttempts: Number.POSITIVE_INFINITY,
  baseDelayMs: 500,
  maxDelayMs: 20_000,
  jitterRatio: 0.5,
};

/**
 * Delay before retry number `attempt` (0-based).
 *
 * Exponential growth is capped at `maxDelayMs`, and jitter only ever *shortens*
 * the delay, so the guaranteed floor still backs off: with the default policy
 * the wait for attempt 3 is between 1s and 2s, never 0.
 */
export function retryDelay(attempt: number, policy: RetryPolicy = DEFAULT_RETRY_POLICY, random: () => number = Math.random): number {
  const safeAttempt = Math.max(0, Math.floor(attempt));
  const exponential = policy.baseDelayMs * 2 ** safeAttempt;
  const ceiling = Math.min(policy.maxDelayMs, exponential);
  const floor = ceiling * (1 - policy.jitterRatio);
  const sample = clamp01(random());
  return Math.round(floor + sample * (ceiling - floor));
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
}

/** Abortable sleep. Rejects with an `AbortError` so callers see one failure mode. */
export function delay(ms: number, signal?: AbortSignal): Promise<void> {
  if (ms <= 0) return signal?.aborted ? Promise.reject(abortError()) : Promise.resolve();
  return new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError());
      return;
    }
    const timer: ReturnType<typeof setTimeout> = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = (): void => {
      clearTimeout(timer);
      reject(abortError());
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

export function abortError(message = "the request was aborted"): Error {
  const error = new Error(message);
  error.name = "AbortError";
  return error;
}
